import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, EMPTY } from 'rxjs';
import {
  FullProjectVersion,
  ProjectVersion,
  VVersionIdBody,
  InlineResponse2002
} from '@app/shared/interfaces';
import { API_BASE } from '../../constants/general.constants';
import { NotebookDefaultFilesService } from '../notebook-default-file/notebook-default-file.service';

@Injectable({
  providedIn: 'root'
})
export class VersionService {
  private http = inject(HttpClient);
  private notebookDefaultFilesService = inject(NotebookDefaultFilesService);


  /**
   * Get a specific version of a project
   * @param projectId The ID of the project
   * @param versionId The ID of the version
   */
  getProjectVersion(projectId: number, versionId: number): Observable<FullProjectVersion> {
    // Guard against undefined/NaN versionId to prevent /v/undefined API requests
    if (!versionId || isNaN(versionId)) {
      console.warn('getProjectVersion called with invalid versionId:', versionId);
      return EMPTY;
    }
    return this.http.get<FullProjectVersion>(`${API_BASE}/project/${projectId}/v/${versionId}`);
  }

  /**
   * Update the default state of a project version
   * @param projectId The ID of the project
   * @param versionId The ID of the version
   * @param data The update data
   */
  updateProjectVersion(projectId: number, versionId: number, data: VVersionIdBody): Observable<ProjectVersion> {
    return this.http.put<ProjectVersion>(`${API_BASE}/project/${projectId}/v/${versionId}`, data);
  }

  /**
   * Export a specific version of a project. The password encrypts admin-only
   * secrets in the export bundle and is ignored by the server for non-admin
   * roles. Operators cannot export (server returns 403).
   * @param projectId The ID of the project
   * @param versionId The ID of the version
   * @param password Optional password (only used by admins to encrypt secrets)
   */
  exportProjectVersion(projectId: number, versionId: number, password?: string): Observable<Blob> {
    const body = password !== undefined ? { password } : {};
    return this.http.post<Blob>(
      `${API_BASE}/project/${projectId}/v/${versionId}/export`,
      body,
      { responseType: 'blob' as 'json' }
    );
  }

  /**
   * Create a new version of a project
   * @param projectId The ID of the project
   * @param majorVersion The major version number
   * @param minorVersion The minor version number
   */
  createProjectVersion(projectId: number, majorVersion: number, minorVersion: number): Observable<InlineResponse2002> {
    return this.http.post<InlineResponse2002>(
      `${API_BASE}/project/${projectId}/v`,
      { majorVersion, minorVersion }
    );
  }

  /**
   * Copy default files from frozen version to new HEAD version
   *
   * This is called after version creation to preserve default files in the new HEAD.
   * Default files are tied to versionId + workflowId in the backend, so when a new
   * HEAD is created, we need to copy files from the frozen version to the new HEAD.
   *
   * @param projectId - Project ID
   * @param frozenVersionId - Old HEAD that was frozen (now has major.minor version)
   * @param headVersionId - New HEAD version (0.0)
   * @returns Promise that resolves when all files are copied
   */
  public async copyDefaultFilesToNewHeadVersion(
    projectId: number,
    frozenVersionId: number,
    headVersionId: number
  ): Promise<void> {
    try {
      console.log(`📋 copyDefaultFilesToNewHeadVersion STARTED:`, {
        projectId,
        frozenVersionId,
        headVersionId,
        frozenVersionIdType: typeof frozenVersionId,
        headVersionIdType: typeof headVersionId
      });

      // 1. Get frozen version with workflows
      console.log(`🔍 Fetching frozen version ${frozenVersionId} from API...`);
      const frozenVersion = await firstValueFrom(
        this.getProjectVersion(projectId, frozenVersionId)
      );
      console.log(`🔍 Frozen version response:`, {
        versionId: frozenVersion?.versionId,
        majorVersion: frozenVersion?.majorVersion,
        minorVersion: frozenVersion?.minorVersion,
        workflowsCount: frozenVersion?.workflows?.length || 0,
        workflows: frozenVersion?.workflows?.map(w => ({ id: w.workflowId, name: w.name }))
      });

      if (!frozenVersion?.workflows?.length) {
        console.warn('⚠️ EARLY RETURN: No workflows in frozen version', frozenVersion);
        return;
      }

      // 2. Get HEAD version with workflows
      console.log(`🔍 Fetching HEAD version ${headVersionId} from API...`);
      const headVersion = await firstValueFrom(
        this.getProjectVersion(projectId, headVersionId)
      );
      console.log(`🔍 HEAD version response:`, {
        versionId: headVersion?.versionId,
        majorVersion: headVersion?.majorVersion,
        minorVersion: headVersion?.minorVersion,
        workflowsCount: headVersion?.workflows?.length || 0,
        workflows: headVersion?.workflows?.map(w => ({ id: w.workflowId, name: w.name }))
      });

      if (!headVersion?.workflows?.length) {
        console.warn('⚠️ EARLY RETURN: No workflows in HEAD version', headVersion);
        return;
      }

      // 3. Create workflow mapping (frozen → HEAD) by name
      // CRITICAL: Workflow IDs change between versions, so we map by name
      const workflowMapping = new Map<number, number>();

      for (const frozenWf of frozenVersion.workflows) {
        const headWf = headVersion.workflows.find((w: any) => w.name === frozenWf.name);
        if (headWf) {
          workflowMapping.set(frozenWf.workflowId, headWf.workflowId);
          console.log(`🔗 Mapped "${frozenWf.name}": ${frozenWf.workflowId} → ${headWf.workflowId}`);
        }
      }

      if (!workflowMapping.size) {
        console.warn('⚠️ No workflow mappings');
        return;
      }

      // 4. Copy default files for each workflow
      // ✅ FIX: Use getDefaultFilesFromVersion to read from the FROZEN version explicitly
      // This avoids the race condition where AppState.headVersionId() may have already
      // been updated to point to the new HEAD (which has no files yet).
      let totalCopied = 0;

      for (const [frozenWfId, headWfId] of workflowMapping) {
        try {
          // ✅ FIX: Get default files from frozen version using explicit versionId
          // This bypasses AppState.headVersionId() which may have already changed
          console.log(`🔍 Calling getDefaultFilesFromVersion for workflow ${frozenWfId}...`, {
            projectId,
            frozenVersionId,
            frozenWfId
          });
          const files = await firstValueFrom(
            this.notebookDefaultFilesService.getDefaultFilesFromVersion(
              projectId, frozenVersionId, frozenWfId
            )
          );
          console.log(`🔍 getDefaultFilesFromVersion returned:`, {
            filesCount: files?.length || 0,
            files: files?.map(f => ({ id: f.fileId, name: f.name }))
          });

          if (!files?.length) {
            console.warn(`⚠️ SKIP: No default files in frozen workflow ${frozenWfId} - continuing to next workflow`);
            continue;
          }

          console.log(`📋 Found ${files.length} files to copy from workflow ${frozenWfId} to ${headWfId}`);

          // Copy each file
          for (const file of files) {
            try {
              // ✅ FIX: Download content from frozen version using explicit versionId
              const content = await firstValueFrom(
                this.notebookDefaultFilesService.getDefaultFileContentsFromVersion(
                  projectId, frozenVersionId, frozenWfId, file.fileId.toString(), 'blob'
                )
              );

              // Upload to HEAD workflow
              const formData = new FormData();
              formData.append('name', file.name);
              formData.append('file', content, file.name);

              await firstValueFrom(
                this.notebookDefaultFilesService.createDefaultFile(
                  projectId, headWfId, formData, true
                )
              );

              totalCopied++;
              console.log(`✅ Copied: ${file.name}`);
            } catch (err) {
              console.error(`❌ Failed to copy ${file.name}:`, err);
            }
          }
        } catch (err) {
          console.error(`❌ Error for workflow ${frozenWfId}:`, err);
        }
      }

      console.log(`✅ copyDefaultFilesToNewHeadVersion COMPLETED:`, {
        totalCopied,
        frozenVersionId,
        headVersionId,
        success: totalCopied > 0
      });

      if (totalCopied === 0) {
        console.warn('⚠️ WARNING: No files were copied! This likely means:');
        console.warn('   1. Frozen version has no default files, OR');
        console.warn('   2. All file copy operations failed, OR');
        console.warn('   3. Workflow mapping failed');
      }
    } catch (err: any) {
      console.error('❌ FATAL ERROR in copyDefaultFilesToNewHeadVersion:', err);
      console.error('❌ Error details:', {
        message: err?.message,
        stack: err?.stack,
        frozenVersionId,
        headVersionId
      });
    }
  }
}
